import React, { useEffect, useRef, useState } from 'react';
import { styles as existingMapStyles } from '../../util/map_utils';

// This preview is isolated from address validation, pricing, and checkout.
// Set to false to restore the original address step without removing code.
export const SHOW_BOOKING_ADDRESS_MAP = true;
const overview = { north: 60, south: 18, west: -130, east: -60 };
const grayscaleStyles = [
  ...existingMapStyles,
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#dedee2' }] },
  { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: '#76767c' }] },
  { featureType: 'poi', elementType: 'labels', stylers: [{ visibility: 'off' }] },
  { featureType: 'transit', elementType: 'labels', stylers: [{ visibility: 'off' }] }
];

export function selectedMapPosition(latitude, longitude, address) {
  if (!address || latitude === '' || longitude === '' || latitude == null || longitude == null) return null;
  const lat = Number(latitude), lng = Number(longitude);
  return Number.isFinite(lat) && Number.isFinite(lng) && Math.abs(lat) <= 90 && Math.abs(lng) <= 180 ? { lat, lng } : null;
}

export default function BookingAddressMap({ latitude, longitude, address }) {
  const node = useRef(null), map = useRef(null), marker = useRef(null);
  const [status, setStatus] = useState('loading');
  useEffect(() => {
    let attempts = 0, interval;
    const initialize = () => {
      if (!window.google?.maps?.Map) {
        if (++attempts >= 50) { clearInterval(interval); setStatus('unavailable'); }
        return;
      }
      clearInterval(interval);
      try {
        map.current = new window.google.maps.Map(node.current, {
          center: { lat: 40, lng: -100 }, zoom: 3, styles: grayscaleStyles,
          disableDefaultUI: true, gestureHandling: 'none', keyboardShortcuts: false,
          clickableIcons: false, backgroundColor: '#eeeef0'
        });
        setStatus('ready');
      } catch (error) { setStatus('unavailable'); }
    };
    interval = setInterval(initialize, 200);
    initialize();
    return () => {
      clearInterval(interval);
      marker.current?.setMap(null);
      if (map.current) window.google?.maps?.event?.clearInstanceListeners(map.current);
      marker.current = null; map.current = null;
    };
  }, []);

  useEffect(() => {
    if (status !== 'ready' || !map.current) return;
    const position = selectedMapPosition(latitude, longitude, address);
    const updateView = () => {
      if (position) { map.current.setZoom(15); map.current.panTo(position); }
      else map.current.fitBounds(overview, 8);
    };
    if (position) {
      if (!marker.current) marker.current = new window.google.maps.Marker({
        map: map.current, position, clickable: false,
        icon: {
          path: 'M0 0C-4-5-11-12-11-19A11 11 0 0 1 11-19C11-12 4-5 0 0ZM0-15A4 4 0 1 0 0-23A4 4 0 1 0 0-15Z',
          fillColor: '#1b264f', fillOpacity: 1, strokeColor: '#ffffff', strokeWeight: 2, scale: 1.4
        }
      });
      marker.current.setPosition(position);
      marker.current.setTitle('Selected property');
    } else { marker.current?.setMap(null); marker.current = null; }
    updateView();
    const observer = window.ResizeObserver ? new window.ResizeObserver(updateView) : null;
    observer?.observe(node.current);
    return () => observer?.disconnect();
  }, [latitude, longitude, address, status]);

  return <div className="booking-address-map" role="region" aria-label={address ? 'Map preview of your selected property' : 'North America map preview'}>
    <div ref={node} className="booking-address-map-canvas" />
    {status !== 'ready' && <p className="booking-address-map-status" role="status">{status === 'loading' ? 'Loading map…' : 'Map preview unavailable. You can still enter your address and continue.'}</p>}
  </div>;
}
